import { getCommandSpec } from '@tunasong/schemas'
import type { Rule } from '@tunasong/schemas'
import type { FC } from 'react'
import { useState } from 'react'
import { Alert } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { Chip } from '../primitives/chip.js'
import { Switch } from '../primitives/switch.js'
import { ConfirmDialog } from '../dialog/confirm-dialog.js'
import { CONVERT_ENTITY_TO_SONG_COMMAND_ID } from './automation-rules.js'
import { describeRule } from './rule-sentence.js'
import styles from './rule-card.module.css'

export interface RuleCardProps {
  rule: Rule
  /** When false the action's server handler / flag isn't live yet — enabling is gated (spec §3). */
  actionAvailable?: boolean
  /** Whether turning the rule on needs a confirm (irreversible action). Defaults from the convert command. */
  irreversible?: boolean
  onToggle: (enabled: boolean) => void
  onTagChange?: (tag: string) => void
  onResume?: () => void
  onDelete?: () => void
  className?: string
}

export const RuleCard: FC<RuleCardProps> = props => {
  const { rule, actionAvailable = false, onToggle, onTagChange, onResume, onDelete, className } = props
  const sentence = describeRule(rule)
  /*
   * `isReversible` on the command's registered spec is the general signal, so a
   * new irreversible action warns without this card learning its id. The convert
   * id remains a fallback for commands whose client-side spec has not registered
   * yet — dropping it would silently stop warning about the one action we ship.
   */
  const irreversible =
    props.irreversible ??
    rule.actions.some(action => {
      const spec = getCommandSpec(action.commandId)
      return spec ? spec.isReversible === false : action.commandId === CONVERT_ENTITY_TO_SONG_COMMAND_ID
    })
  const paused = rule.pausedReason === 'loop-guard'

  const [pendingEnable, setPendingEnable] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftTag, setDraftTag] = useState(sentence.tag ?? '')

  const actionLabel = actionAvailable ? sentence.actionLabel : 'Convert to Song — not available yet'
  const trigger = sentence.isFocus ? sentence.eventLabel : `When a note is ${sentence.eventLabel} ${sentence.tag ?? ''}`.trim()
  const ariaLabel = `${trigger}, ${sentence.actionLabel}. Currently ${rule.enabled ? 'on' : 'off'}.`

  const handleToggle = (checked: boolean) => {
    if (checked && irreversible) {
      setPendingEnable(true)
      return
    }
    onToggle(checked)
  }

  const commitTag = () => {
    setEditing(false)
    const next = draftTag.trim()
    if (next && next !== sentence.tag) {
      onTagChange?.(next)
    }
  }

  const cardClass = [styles.card, className].filter(Boolean).join(' ')

  return (
    <div role="group" aria-label={ariaLabel} className={cardClass}>
      <div className={styles.sentence}>
        {sentence.isFocus ? <strong>{sentence.eventLabel}</strong> : <span>When a note is</span>}
        {sentence.isFocus ? null : <strong>{sentence.eventLabel}</strong>}
        {sentence.tag !== undefined && !sentence.isFocus ? (
          editing ? (
            <input
              aria-label="Tag"
              className={styles.tagInput}
              value={draftTag}
              onChange={event => setDraftTag(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  commitTag()
                }
                if (event.key === 'Escape') {
                  setEditing(false)
                  setDraftTag(sentence.tag ?? '')
                }
              }}
              onBlur={commitTag}
            />
          ) : (
            <>
              <Chip label={sentence.tag} />
              {onTagChange ? (
                <Button
                  aria-label="Edit tag"
                  onClick={() => {
                    setDraftTag(sentence.tag ?? '')
                    setEditing(true)
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </>
          )
        ) : null}
        <span aria-hidden="true">→</span>
        <strong>{actionLabel}</strong>
      </div>

      {irreversible ? <p className={styles.caption}>Converting can’t be undone.</p> : null}
      {!actionAvailable ? <p className={styles.helper}>This action is coming soon.</p> : null}

      {paused ? (
        <Alert
          severity="warning"
          aria-live="polite"
          action={<Button onClick={() => onResume?.()}>Turn back on</Button>}
        >
          <strong>Paused to prevent a loop.</strong> This rule tried to run too many times in a row, so we paused it.
          Turn it back on if this was expected.
        </Alert>
      ) : null}

      <div className={styles.controls}>
        <Switch
          checked={rule.enabled}
          disabled={!actionAvailable}
          onCheckedChange={handleToggle}
          aria-label={`Turn on: ${sentence.actionLabel}`}
        />
        <span className={styles.state}>{rule.enabled ? 'On' : 'Off'}</span>
        {onDelete ? (
          <Button aria-label="Delete rule" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={pendingEnable}
        title="Turn on this rule?"
        text="It will convert matching notes to Songs automatically. Converting a note to a Song can’t be undone."
        okText="Turn on"
        cancelText="Cancel"
        onConfirm={() => {
          setPendingEnable(false)
          onToggle(true)
        }}
        onCancel={() => setPendingEnable(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this rule?"
        text="It will stop running. You can set it up again later."
        okText="Delete"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmDelete(false)
          onDelete?.()
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  )
}
